import { useCallback, useMemo, useRef, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { PatientDemographicsPanel } from '@features/encounters/components/v2/PatientDemographicsPanel';
import { usePatientClinicalInfoQuery } from '@features/patients';
import { useAutoSave } from '@hooks/useAutoSave';
import RefreshIcon from '@mui/icons-material/Refresh';
import { alpha, Box, Button, CircularProgress, Paper, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import type { Patient } from '@ts/types';

import {
  useGetEncounterEligibilityQuery,
  useGetPatientEligibilityQuery,
  useOverrideEligibilityMutation,
  useRunEligibilityCheckMutation,
  useRunPatientEligibilityCheckMutation,
} from '../../store/api';
import type {
  EligibilitySnapshot,
  ManualUpdateRequest,
} from '../../types';
import {
  apiToSnapshot,
  LIGHT_TO_API_STATUS,
} from '../../types';

import { CoverageDetailsGrid, type CoverageField, type CoverageOverrides } from './CoverageDetailsGrid';
import { EligibilityStatusBanner } from './EligibilityStatusBanner';
import { formatAlertLabel, getAlertGuidance, STATUS_CONFIG } from './eligibilityStatusConfig';
import { ManualOverrideFooter, type VerificationMethod } from './ManualOverrideFooter';

// ── Component ──────────────────────────────────────────────────────────

interface EligibilityBlockProps {
  patientId: string;
  encounterId?: string;
  defaultCollapsed?: boolean;
}

export function EligibilityBlock({
  patientId,
  encounterId,
}: EligibilityBlockProps) {
  const theme = useTheme();

  // ── API hooks ──────────────────────────────────────────────────────
  const {
    data: encounterApiData,
    isLoading: isEncounterLoading,
  } = useGetEncounterEligibilityQuery(
    { encounterId: encounterId ?? '' },
    { skip: !encounterId },
  );

  const {
    data: patientApiData,
    isLoading: isPatientLoading,
  } = useGetPatientEligibilityQuery(
    { patientId },
    { skip: !patientId || !!encounterId },
  );

  const apiData = encounterApiData ?? patientApiData;
  const isLoading = isEncounterLoading || isPatientLoading;

  const [overrideEligibility] = useOverrideEligibilityMutation();

  // Re-run mutations
  const [triggerEncounterCheck, { isLoading: isEncounterRerunning }] =
    useRunEligibilityCheckMutation();
  const [triggerPatientCheck, { isLoading: isPatientRerunning }] =
    useRunPatientEligibilityCheckMutation();
  const isRerunning = isEncounterRerunning || isPatientRerunning;

  const handleRerun = useCallback(() => {
    if (encounterId) {
      triggerEncounterCheck({ encounterId });
    } else {
      triggerPatientCheck({ patientId });
    }
  }, [encounterId, patientId, triggerEncounterCheck, triggerPatientCheck]);

  // Demographics — shown when there's a demographics mismatch alert
  const hasDemographicsAlert = useMemo(() => {
    const alerts = apiData?.alerts ?? [];
    return alerts.some(a => {
      const upper = a.toUpperCase();
      return upper.includes('DEMOGRAPHICS');
    });
  }, [apiData?.alerts]);

  const { data: clinicalInfoResponse, isLoading: isDemoLoading } =
    usePatientClinicalInfoQuery({ patientId }, { skip: !patientId || !hasDemographicsAlert });

  const patient = useMemo((): Patient | null => {
    if (!clinicalInfoResponse?.patient_clinical_info) return null;
    const info = clinicalInfoResponse.patient_clinical_info;
    return {
      patient_id: info.patient_id,
      first_name: info.first_name ?? null,
      last_name: info.last_name ?? null,
      preferred_name: info.preferred_name ?? null,
      middle_name: info.middle_name ?? null,
      dob: info.dob ?? null,
      gender: info.gender ?? null,
      pronouns: info.pronouns ?? null,
      email: info.email ?? null,
      mobile_phone: info.mobile_phone ?? null,
      phone: info.mobile_phone ?? null,
    } as Patient;
  }, [clinicalInfoResponse]);

  // ── Local state ────────────────────────────────────────────────────
  const [localSnapshot, setLocalSnapshot] = useState<EligibilitySnapshot | null>(null);
  const [hasLocalOverride, setHasLocalOverride] = useState(false);
  const [isOverrideMode, setIsOverrideMode] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState<VerificationMethod>('');
  const [coverageOverrides, setCoverageOverrides] = useState<CoverageOverrides>({});
  const pendingDataRef = useRef<EligibilitySnapshot | null>(null);

  // Derive the effective snapshot — API data wins unless user has local edits
  const snapshot = useMemo<EligibilitySnapshot | null>(() => {
    if (hasLocalOverride && localSnapshot) return localSnapshot;
    if (apiData) return apiToSnapshot(apiData);
    return localSnapshot;
  }, [hasLocalOverride, localSnapshot, apiData]);

  // ── Autosave ───────────────────────────────────────────────────────
  const performSave = useCallback(
    async (data: EligibilitySnapshot) => {
      if (!encounterId) return;
      const manualUpdateRequest: ManualUpdateRequest = {
        status: LIGHT_TO_API_STATUS[data.status_light],
        summary: data.summary,
        ...(verificationMethod ? { method: verificationMethod } : {}),
        ...coverageOverrides,
      };
      await overrideEligibility({ encounterId, manualUpdateRequest }).unwrap();
    },
    [encounterId, overrideEligibility, verificationMethod, coverageOverrides],
  );

  const { triggerAutoSave, saveImmediately, status, lastSavedAt } =
    useAutoSave<EligibilitySnapshot>({
      onSave: performSave,
      debounceMs: 2000,
      flushOnUnmount: true,
    });

  const handleOverrideChange = useCallback(
    (field: 'status_light' | 'summary', value: string) => {
      if (!snapshot) return;
      const updated = { ...snapshot, [field]: value };
      setLocalSnapshot(updated);
      setHasLocalOverride(true);
      pendingDataRef.current = updated as EligibilitySnapshot;

      if (field === 'summary') {
        triggerAutoSave(updated);
      } else {
        saveImmediately(updated);
      }
    },
    [snapshot, triggerAutoSave, saveImmediately],
  );

  const handleCoverageFieldChange = useCallback(
    (field: CoverageField, value: string | number | null) => {
      if (!snapshot) return;
      setCoverageOverrides((prev) => ({ ...prev, [field]: value }));
      setHasLocalOverride(true);
      // Debounce for number inputs, save on blur
      const current = pendingDataRef.current ?? snapshot;
      pendingDataRef.current = current;
      triggerAutoSave(current);
    },
    [snapshot, triggerAutoSave],
  );

  const handleBlur = useCallback(() => {
    if (pendingDataRef.current) {
      saveImmediately(pendingDataRef.current);
      pendingDataRef.current = null;
    }
  }, [saveImmediately]);

  const handleToggleOverride = useCallback(() => {
    setIsOverrideMode((prev) => !prev);
  }, []);

  // ── Styling ──────────────────────────────────────────────────────────
  const statusConfig = snapshot ? STATUS_CONFIG[snapshot.status_light] : null;
  const paletteColor = statusConfig ? theme.palette[statusConfig.palette].main : undefined;
  const StatusIcon = statusConfig?.icon;

  const cardSx = {
    border: '1px solid',
    borderColor: paletteColor ? alpha(paletteColor, 0.4) : 'divider',
    borderRadius: 2,
    p: 2.5,
    ...(paletteColor ? { boxShadow: `0 0 0 1px ${alpha(paletteColor, 0.25)}` } : {}),
  };

  // ── Render ─────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={32} width={180} />
          <Skeleton variant="rounded" height={48} />
          <Skeleton variant="rounded" height={80} />
          <Skeleton variant="rounded" height={48} />
        </Stack>
      </Paper>
    );
  }

  if (!snapshot || !apiData) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Eligibility
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No eligibility data available. Run an eligibility check from an encounter to see
          results here.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={cardSx}>
      {/* ── Header ────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {StatusIcon && <StatusIcon sx={{ color: paletteColor, fontSize: 22 }} />}
        <Typography variant="subtitle1" fontWeight={600}>
          Eligibility
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {statusConfig!.label}
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="text"
            size="small"
            onClick={handleRerun}
            disabled={isRerunning}
            startIcon={isRerunning ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
          >
            Re-run
          </Button>
          <AutosaveStatus
            status={status}
            isDirty={pendingDataRef.current !== null}
            lastSavedAt={lastSavedAt}
            verbose
          />
        </Box>
      </Box>

      {/* ── A: Guidance banner ────────────────────────────────────── */}
      <EligibilityStatusBanner statusLight={snapshot.status_light} />

      {/* ── B: Alert guidance ────────────────────────────────────── */}
      {snapshot.alerts.length > 0 && (
        <Stack spacing={0.75} sx={{ mb: 2 }}>
          {snapshot.alerts.map((alert) => {
            const guidance = getAlertGuidance(alert);
            return (
              <Typography key={alert} variant="body2" color="text.secondary">
                {guidance ?? formatAlertLabel(alert)}
              </Typography>
            );
          })}
        </Stack>
      )}

      {/* ── B (cont): Read-only summary if present and not overriding */}
      {!isOverrideMode && snapshot.summary && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {snapshot.summary}
        </Typography>
      )}

      {/* ── C: Demographics fix area (conditional) ────────────────── */}
      {hasDemographicsAlert && (
        <Box
          sx={{
            bgcolor: alpha(theme.palette.warning.light, 0.1),
            borderRadius: 1,
            p: 2,
            border: '1px solid',
            borderColor: alpha(theme.palette.warning.main, 0.3),
            mb: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Review Patient Demographics
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The payer reported a mismatch with the patient information on file.
            Review the demographics below and correct any errors (e.g. name spelling, date of birth),
            then re-run the eligibility check.
          </Typography>
          <PatientDemographicsPanel
            patient={patient}
            isLoading={isDemoLoading}
          />
        </Box>
      )}

      {/* ── D: Coverage Details ────────────────────────────────────── */}
      <CoverageDetailsGrid
        data={apiData}
        isOverrideMode={isOverrideMode}
        overrides={coverageOverrides}
        onFieldChange={handleCoverageFieldChange}
        onBlur={handleBlur}
      />

      {/* ── E: Timestamps + manual override prompt ────────────────── */}
      <ManualOverrideFooter
        snapshot={snapshot}
        isOverrideMode={isOverrideMode}
        verificationMethod={verificationMethod}
        onToggleOverride={handleToggleOverride}
        onOverrideChange={handleOverrideChange}
        onMethodChange={setVerificationMethod}
        onBlur={handleBlur}
      />
    </Paper>
  );
}
